import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  HStack,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useCreateScenario, useUpdateScenario } from '../hooks/useScenarios';
import type { PlaybackMode, Scenario } from '../types';

interface ScenarioFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  scenario?: Scenario;
  onSaved?: (scenario: Scenario) => void;
}

export function ScenarioFormModal({ isOpen, onClose, scenario, onSaved }: ScenarioFormModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = !!scenario;
  const create = useCreateScenario();
  const update = useUpdateScenario(scenario?.id ?? '');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>('sequential');

  useEffect(() => {
    if (isOpen) {
      setName(scenario?.name ?? '');
      setDescription(scenario?.description ?? '');
      setPlaybackMode(scenario?.playbackMode ?? 'sequential');
    }
  }, [isOpen, scenario]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      const payload = { name: name.trim(), description: description.trim() || undefined, playbackMode };
      const result = isEdit ? await update.mutateAsync(payload) : await create.mutateAsync(payload);
      toast({ status: 'success', title: isEdit ? t('scenarios.saved') : t('scenarios.created'), duration: 2000 });
      onSaved?.(result);
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        title: t('common.error', 'Something went wrong'),
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? t('scenarios.edit') : t('scenarios.create')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>{t('scenarios.name')}</FormLabel>
              <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} autoFocus />
            </FormControl>
            <FormControl>
              <FormLabel>{t('scenarios.description')}</FormLabel>
              <Textarea value={description} maxLength={1000} rows={3} onChange={(e) => setDescription(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('scenarios.playbackMode')}</FormLabel>
              <RadioGroup value={playbackMode} onChange={(v) => setPlaybackMode(v as PlaybackMode)}>
                <HStack spacing={6}>
                  <Radio value="sequential" colorScheme="brand">{t('scenarios.playbackModes.sequential')}</Radio>
                  <Radio value="random" colorScheme="brand">{t('scenarios.playbackModes.random')}</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={create.isPending || update.isPending}
            isDisabled={!name.trim()}
          >
            {isEdit ? t('common.save') : t('scenarios.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ScenarioFormModal;
